export interface Address {
  cep: string;
  logradouro: string;
  numero: string;
  complemento: string;
  bairro: string;
  cidade: string;
  estado: string;
}

export interface Contact {
  nome: string;
  telefone: string;
  relacao: string;
  observacao: string;
  photoFile: File | null;
  photoUrl: string | null;
  conversationFile: File | null;
  conversationUrl: string | null;
}

export interface UploadedDocument {
  category: string;
  label: string;
  files: File[];
  urls: string[];
  uploading: boolean;
}

export interface LoanData {
  amount: string;
  installments: string;
  interestRate: string;
  isCustom: boolean;
  installmentDates: Date[];
  ruta: number;
}

export interface ClientPersonalData {
  name: string;
  cpf: string;
  phone: string;
  pixKey: string;
  bank: string;
  serviceEntryTime: string;
  serviceExitTime: string;
}

export type ClientType = 'clt' | 'autonomous';

export interface ManualRegistrationData {
  clientType: ClientType;
  loan: LoanData;
  personal: ClientPersonalData;
  enderecoResidencial: Address;
  enderecoComercial: Address;
  contacts: Contact[];
  documents: UploadedDocument[];
  declarationConfirmed: boolean;
}

export const DOCUMENT_CATEGORIES = [
  { category: 'selfie_com_documento', label: 'Selfie com documento original (RG ou CNH)' },
  { category: 'video_entrando_casa', label: 'Vídeo entrando e saindo de casa falando o nome da vendedora' },
  { category: 'carteira_trabalho', label: 'Carteira de Trabalho exportada do app (hoje)' },
  { category: 'rg_cnh_frente', label: 'Foto RG ou CNH — frente' },
  { category: 'rg_cnh_verso', label: 'Foto RG ou CNH — verso' },
  { category: 'holerite_1', label: '1° Holerite recente' },
  { category: 'holerite_2', label: '2° Holerite recente' },
  { category: 'holerite_3', label: '3° Holerite recente' },
  { category: 'comprovante_endereco', label: 'Comprovante de endereço em nome do cliente' },
  { category: 'foto_local_trabalho', label: 'Foto do local de trabalho' },
  { category: 'foto_frente_residencia', label: 'Foto da frente da residência' },
  { category: 'print_instagram', label: 'Print do perfil do Instagram' },
  { category: 'print_facebook', label: 'Print do perfil do Facebook' },
];

/**
 * Parses the stored address string format:
 * "Logradouro, Numero[, Complemento] - Bairro, Cidade/UF - CEP: XXXXX-XXX"
 * Falls back to empty strings for any field that can't be reliably extracted.
 */
export function parseAddress(addressStr: string, complement?: string | null): Address {
  const empty = emptyAddress();
  if (!addressStr || addressStr.trim() === '') return empty;

  try {
    // Extract CEP from "CEP: XXXXX-XXX" at the end
    const cepMatch = addressStr.match(/CEP:\s*([\d]{5}-[\d]{3})/);
    const cep = cepMatch ? cepMatch[1] : '';

    // Remove the " - CEP: ..." suffix
    const withoutCep = addressStr.replace(/\s*-\s*CEP:.*$/, '').trim();

    // Split by " - " to get parts: ["Logradouro, Numero[, Complemento]", "Bairro, Cidade/UF"]
    // There may be extra " - " segments for entries without proper data
    const dashParts = withoutCep.split(/\s+-\s+/);

    // Last meaningful dash part should contain "Cidade/UF"
    let cidadeEstado = '';
    let bairro = '';
    let firstPart = '';

    for (let i = dashParts.length - 1; i >= 0; i--) {
      if (dashParts[i].includes('/')) {
        cidadeEstado = dashParts[i].trim();
        bairro = dashParts.slice(1, i).join(' - ').trim();
        firstPart = dashParts[0].trim();
        break;
      }
    }

    if (!firstPart) firstPart = dashParts[0]?.trim() || '';

    // Extract cidade and estado from "Cidade/UF"
    const slashIdx = cidadeEstado.lastIndexOf('/');
    const cidade = slashIdx >= 0 ? cidadeEstado.slice(0, slashIdx).trim() : '';
    const estado = slashIdx >= 0 ? cidadeEstado.slice(slashIdx + 1).trim() : '';

    // Parse firstPart: "Logradouro, Numero[, Complemento]"
    const commaParts = firstPart.split(',').map(s => s.trim());
    const logradouro = commaParts[0] || '';
    const numero = commaParts[1] || '';
    // Ignore embedded complement from the string — use the dedicated column instead

    return {
      cep,
      logradouro,
      numero,
      complemento: complement?.trim() || '',
      bairro,
      cidade,
      estado,
    };
  } catch {
    return empty;
  }
}

export const emptyAddress = (): Address => ({
  cep: '',
  logradouro: '',
  numero: '',
  complemento: '',
  bairro: '',
  cidade: '',
  estado: '',
});

export const emptyContact = (): Contact => ({
  nome: '',
  telefone: '',
  relacao: '',
  observacao: '',
  photoFile: null,
  photoUrl: null,
  conversationFile: null,
  conversationUrl: null,
});

export function formatCurrency(value: number | string): string {
  const num = typeof value === 'string' ? parseFloat(value.replace(/[^\d.-]/g, '')) : value;
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(num);
}

export function getInterestRateForInstallments(installments: string): string {
  switch (installments) {
    case '1': return '70';
    case '2': return '80';
    case '3': return '90';
    default: return '70';
  }
}

export const AUTONOMOUS_DOCUMENT_CATEGORIES = [
  { category: 'selfie_com_documento', label: 'Selfie com documento original (RG ou CNH)' },
  { category: 'video_entrando_casa', label: 'Vídeo entrando e saindo de casa falando o nome da vendedora' },
  { category: 'rg_cnh_frente', label: 'Foto RG ou CNH — frente' },
  { category: 'rg_cnh_verso', label: 'Foto RG ou CNH — verso' },
  { category: 'extrato_bancario_1', label: 'Extrato bancário — mês 1' },
  { category: 'extrato_bancario_2', label: 'Extrato bancário — mês 2' },
  { category: 'extrato_bancario_3', label: 'Extrato bancário — mês 3' },
  { category: 'comprovante_endereco', label: 'Comprovante de endereço em nome do cliente' },
  { category: 'foto_local_trabalho', label: 'Foto do local de trabalho / comércio' },
  { category: 'foto_frente_residencia', label: 'Foto da frente da residência' },
  { category: 'print_instagram', label: 'Print do perfil do Instagram' },
  { category: 'print_facebook', label: 'Print do perfil do Facebook' },
];

export function calculateAutonomousLoanDetails(amount: string, installments: string, interestRate: string) {
  const principal = parseFloat(amount) || 0;
  const numInstallments = parseInt(installments);
  const rate = parseFloat(interestRate) / 100;

  const baseInstallmentAmount = principal / numInstallments;
  const installmentAmount = baseInstallmentAmount * (1 + rate);
  const totalAmount = installmentAmount * numInstallments;

  return {
    amountReceived: principal,
    baseInstallmentAmount,
    installmentAmount,
    totalAmount,
  };
}

export function calculateLoanDetails(amount: string, installments: string, interestRate: string) {
  const principal = parseFloat(amount) || 0;
  const initialFee = 10;
  const discountFee = 10;
  const numInstallments = parseInt(installments);
  const rate = parseFloat(interestRate) / 100;

  const amountReceived = principal - discountFee;
  const amountWithFee = principal + initialFee;
  const baseInstallmentAmount = amountWithFee / numInstallments;
  const installmentAmount = baseInstallmentAmount * (1 + rate);
  const totalAmount = installmentAmount * numInstallments;

  return {
    amountReceived,
    initialFee,
    discountFee,
    amountWithFee,
    baseInstallmentAmount,
    installmentAmount,
    totalAmount,
  };
}
